"use client";

import Link from "next/link";
import { usePostHog } from "posthog-js/react";

/**
 * The "see a sample" block on the /free/* document pages.
 *
 * It replaces <LeadMagnetForm>, which asked for an email address in exchange
 * for the sample. The gate held nothing back — the same PDFs are public
 * (/samples embeds all five, the homepage links the RAMS one directly) — and
 * no list, sequence or person ever used the address it collected. On
 * /free/cpp-template it also promised a "CDM compliance checklist" and then
 * emailed the sample CPP. Linking the file directly hands the visitor the
 * document at once and collects nothing.
 *
 * The `document` property on `sample_downloaded` uses SamplesViewer's ids
 * (app/(marketing)/samples/samples-viewer.tsx), so the event reads the same
 * whichever page the download came from; `source` says which page.
 *
 * The map is deliberately not exported: this is a "use client" module, so a
 * server-side importer would receive a client reference, not the paths. The
 * unit test pins the paths independently.
 */
const SAMPLE_PDF_FILES = {
  rams: "/samples/sample-rams.pdf",
  cpp: "/samples/sample-cpp.pdf",
  "site-induction": "/samples/sample-site-induction.pdf",
} as const;

export type SamplePdf = keyof typeof SAMPLE_PDF_FILES;

export function SamplePdfDownload({
  sample,
  source,
  heading,
  description,
}: {
  sample: SamplePdf;
  /** PostHog `source`, e.g. "free_rams_generator". */
  source: string;
  heading: string;
  description: string;
}) {
  const posthog = usePostHog();

  return (
    <div className="rounded-xl border border-border bg-surface p-6 sm:p-8">
      <h3 className="text-lg font-bold text-navy">{heading}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted">{description}</p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <a
          href={SAMPLE_PDF_FILES[sample]}
          download
          onClick={() =>
            posthog?.capture?.("sample_downloaded", { source, document: sample })
          }
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark"
        >
          Download the sample PDF
        </a>
        <Link
          href="/samples"
          className="inline-flex min-h-[44px] items-center text-sm font-semibold text-brand-700 hover:underline"
        >
          See all sample documents &rarr;
        </Link>
      </div>
    </div>
  );
}
